import { type AfterFetchContext, watchDebounced } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, ref, shallowRef, watch } from "vue";
import { type LocationQuery, useRoute, useRouter } from "vue-router";

import type { SortDirection } from "@/components/archive/NewsFeed/ActionBarOptions.vue";
import {
  type SearchConfig,
  useSearchResults,
} from "@/composition/api/content.ts";
import useSearchResultsItems from "@/composition/shared/useSearchResultsItems.ts";
import { useTracking } from "@/composition/shared/useTracking.ts";
import type {
  ContentBaseItem,
  ContentItemSearchResponse,
  FilterRule,
} from "@/types/content.d.ts";
import { FilterType } from "@/types/enums.ts";

import useAutoUpdatesStore from "./search/autoUpdates.ts";
import useFilterSelectorStore from "./search/filterSelector.ts";
import useQuerySearch from "./search/querySearch.ts";
import useSavedSearchesStore from "./search/savedSearches.ts";
import useSidebarLayoutStore from "./sidebarLayoutStore.ts";

export interface IMeta {
  lastUpdated: Date | null;
  hasActiveSearchUpdateSubscription: boolean;
}

export interface PresetRange {
  label: string;
  range: [Date, Date] | { year: number; month: number };
  name?: string;
}

export type DateType =
  | string
  | Date
  | [Date, Date]
  | {
      month: number;
      year: number;
    };

/**
 * Maps the current route query (filter selections, incl. the date filter) plus
 * the free-text search query into the FilterRule[] shape the search API expects.
 * Kept as a standalone, pure function so it's testable without spinning up the
 * store's reactive watchers.
 */
export function mapQueryToFilterRules(
  query: LocationQuery,
  searchQuery: string,
  selectedFilterFields: string[],
): FilterRule[] {
  const filters = Object.entries(query).flatMap(([key, value]) => {
    if (Object.values(FilterType).includes(key as FilterType)) {
      return {
        type: key,
        values: Array.isArray(value)
          ? (value as string[])
          : ([value] as string[]),
        metaDataTags: Array.isArray(value)
          ? (value as string[])
          : ([value] as string[]),
      };
    } else {
      return [];
    }
  });
  return [
    {
      type: FilterType.QUERY,
      values: [searchQuery],
      metaDataTags: null,
      fields: selectedFilterFields,
      exclude: false,
    },
    ...filters,
  ].filter(
    (rule) =>
      rule.values &&
      rule.values.length > 0 &&
      rule.values.every((value) => value && value.length > 0),
  );
}

/**
 * The search store is for the archive page, it has some specific requirements for that
 * if things from the searchStore are needed outside the store it may be helpful to create composables or abstract them in their own store.
 */
export const useSearchStore = defineStore("search", () => {
  const autoUpdatesStore = useAutoUpdatesStore();
  const querySearch = useQuerySearch();
  const filterSelectorStore = useFilterSelectorStore();
  const savedSearchStore = useSavedSearchesStore();
  const sidebarLayoutStore = useSidebarLayoutStore();
  const { trackSearchCount } = useTracking();
  const router = useRouter();
  const route = useRoute();

  const { trackEvent } = useTracking();

  const autoRefresh = ref(true);
  const activeSearchUpdateId = shallowRef<string | null>(null);

  function setAutoRefresh(newState: boolean) {
    autoRefresh.value = newState;
  }

  watch(autoRefresh, async (newValue, oldValue) => {
    if (newValue === oldValue) return;

    if (!oldValue && newValue) {
      await executeSearch();
    }

    if (newValue) {
      autoUpdatesStore.addSearchUpdate(activeSearchUpdateId.value);
    } else if (!newValue) {
      autoUpdatesStore.removeSearchUpdate(activeSearchUpdateId.value);
    }
  });

  watchDebounced(
    [
      () => querySearch.searchQuery,
      () => querySearch.selectedFilterFields,
      () => filterSelectorStore.routeQuery,
      () => savedSearchStore.activeSavedSearchId,
    ],
    async (
      [newSearchQuery, newFilterFields, newFilters, newSavedSearchId],
      [oldSearchQuery, oldFilterFields, oldFilters, oldSavedSearchId],
    ) => {
      const newUrls = {
        search: newSearchQuery.length > 0 ? newSearchQuery : undefined, // changing the param name will potentially break Matomo search tracking https://matomo.org/faq/reports/enable-site-search-tracking-for-your-website/
        filterFields: newFilterFields,
        ...newFilters,
        savedSearchId: newSavedSearchId ? newSavedSearchId : undefined,
      };

      const oldUrls = {
        search: oldSearchQuery,
        filterFields: oldFilterFields,
        ...oldFilters,
        savedSearchId: oldSavedSearchId,
      };
      if (JSON.stringify(newUrls) === JSON.stringify(oldUrls)) return;
      await router.replace({
        query: newUrls,
      });
    },
    {
      debounce: 50,
      deep: true,
      immediate: true,
    },
  );

  const filterRules = ref<FilterRule[]>([]);

  const searchContinuation = ref<{ [key: string]: any }[]>([]);
  const sortDirection = ref<SortDirection>("desc");
  watch(sortDirection, async (newDirection, oldDirection) => {
    if (newDirection === "asc") {
      setAutoRefresh(false);
    }

    if (newDirection === "desc" && oldDirection === "asc") {
      setAutoRefresh(true);
    }

    if (newDirection !== oldDirection) {
      await executeSearch();
    }

    trackEvent("listView_click_listItem_sorting", newDirection);
  });
  const sortSpecs = computed(() => {
    return [{ field: "display_date_time", direction: sortDirection.value }];
  });

  const searchConfig = computed<SearchConfig>(() => {
    return {
      filterRules: [
        { type: FilterType.MEDIATYPE, values: ["topic"], exclude: true },
        ...filterRules.value,
      ],
      highlightSpecs: querySearch.highlightSpecs,
      sortSpecs: sortSpecs.value,
      maxResults: 15,
      requestSearchUpdate: autoRefresh.value,
      searchContinuationFields: isLoadingMore.value
        ? searchContinuation.value
        : [],
    };
  });

  const searchResultsCount = ref(0);
  watch(searchResultsCount, async (newSearchResultsCount) => {
    trackSearchCount(newSearchResultsCount);
  });

  const { execute, isFinished: searchRequestFinished } = useSearchResults(
    searchConfig,
    {
      immediate: false,
      afterFetch(
        context: AfterFetchContext<ContentItemSearchResponse>,
      ): AfterFetchContext<ContentItemSearchResponse> {
        if (context.data) {
          searchContinuation.value = context.data.searchContinuationFields;
          activeSearchUpdateId.value = context.data.searchUpdateId;
          if (isLoadingMore.value) {
            loadMoreItems.value = [
              ...loadMoreItems.value,
              ...context.data.contentItemList,
            ];
          } else {
            initialSearchResults.value = context.data.contentItemList;
            loadMoreItems.value = [];
          }
        }
        searchResultsCount.value = context.data?.totalResults ?? 0;
        return context;
      },
    },
  );

  watch(activeSearchUpdateId, async (newValue, oldValue) => {
    if (newValue) {
      if (newValue !== oldValue) {
        if (oldValue !== null) {
          await autoUpdatesStore.removeSearchUpdate(oldValue);
        }
      }
      await autoUpdatesStore.addSearchUpdate(newValue);
    }
  });

  function resetPaginationState() {
    searchContinuation.value = [];
    loadMoreItems.value = [];
    initialSearchResults.value = [];
    loadMoreCounter.value = 0;
    setIsLoadingMore(false);
  }

  async function executeSearch() {
    if (!isLoadingMore.value) {
      resetPaginationState();
    }

    autoUpdatesStore.clearIsNew(activeSearchUpdateId.value);
    autoUpdatesStore.clearContentItems(activeSearchUpdateId.value);
    await execute();
  }

  const initialSearchResults = ref<ContentBaseItem[]>([]);
  const loadMoreItems = ref<ContentBaseItem[]>([]);

  const socketItems = computed(() => {
    if (!activeSearchUpdateId.value) return [];
    const searchUpdate = autoUpdatesStore.getSearchUpdate(
      activeSearchUpdateId.value,
    );
    if (!searchUpdate) return [];
    return searchUpdate.contentItems;
  });

  /**
   * Merge search results from initialResults and loadMoreResults and filter duplicates by URN
   */
  const searchResults = useSearchResultsItems(
    initialSearchResults,
    socketItems,
    loadMoreItems,
    sortDirection,
  );

  const isLoadingMore = ref(false);
  const loadMoreCounter = ref(0);
  function setIsLoadingMore(state: boolean) {
    isLoadingMore.value = state;
  }

  function resetSearch() {
    autoUpdatesStore.clearIsNew(activeSearchUpdateId.value);
    querySearch.editSearchQuery("");
    querySearch.resetSearchQuery();
    filterSelectorStore.resetFilters();
    savedSearchStore.resetActiveSavedSearchId();
  }

  // set to true whenever the filter rules are changed and aren't zero
  // set to false whenever a search was saved or a saved search was clicked
  // after its false we change it whenever any of the filter rules is changed again
  // whenever any of the filter rules has changed after this was set to true it
  const showSaveSearchButton = ref(false);

  function setShowSaveSearchButton(newState: boolean) {
    showSaveSearchButton.value = newState;
  }

  /**
   * Need so the search is triggered on first load, the condition prevents execution when nothing has changed
   * Previously the opening of a FeedDetailPage has caused a search execution as well because a route.query change was triggered
   * but the queries were the same.
   */
  function resetScrollPosition() {
    if (sidebarLayoutStore.mainRef?.scrollTo) {
      sidebarLayoutStore.mainRef.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
      return;
    }

    if (
      typeof window !== "undefined" &&
      typeof window.scrollTo === "function"
    ) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }

  watchDebounced(
    () => route.query,
    async (newVal, oldVal, onCleanup) => {
      const controller = new AbortController();
      if (
        JSON.stringify(newVal) === JSON.stringify(oldVal) ||
        Object.keys(newVal).length === 0
      )
        return;
      resetScrollPosition();
      filterRules.value = mapQueryToFilterRules(
        newVal,
        querySearch.searchQuery,
        querySearch.selectedFilterFields,
      );
      if (
        filterRules.value.length == 0 ||
        savedSearchStore.activeSavedSearchId
      ) {
        setShowSaveSearchButton(false);
      } else if (!savedSearchStore.activeSavedSearchId) {
        setShowSaveSearchButton(true);
      }
      await executeSearch();
      // for when query changes before the request completes
      onCleanup(() => {
        controller.abort();
      });
    },
    { debounce: 300, maxWait: 1000, immediate: true, deep: true },
  );

  const searchResultsUrns = computed(() => {
    return searchResults.value.map((item) => item.urn);
  });

  return {
    searchConfig,
    searchResults,
    searchResultsCount,
    searchResultsUrns,
    executeSearch,
    searchContinuation,
    searchRequestFinished,
    isLoadingMore,
    setIsLoadingMore,
    loadMoreCounter,
    resetSearch,
    sortDirection,
    setShowSaveSearchButton,
    showSaveSearchButton,
    activeSearchUpdateId,
    autoRefresh,
    setAutoRefresh,
    socketItems,
  };
});
